import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Loader2, ArrowLeft, UserPlus, LogIn, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { authApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { FooterBranded } from '../components/Footer';

import { getApiBase, getAssetUrl } from '../lib/assetUrl';

const API_BASE = getApiBase();

export default function ClientLoginPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // Success state after registration
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/company/public/${companySlug}`);
        if (!response.ok) {
          throw new Error('Entreprise non trouvée');
        }
        const data = await response.json();
        setCompanyData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (companySlug) {
      fetchCompanyData();
    }
  }, [companySlug]);

  // Handle URL params: ?action=register|login and ?email=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const email = params.get('email');
    if (action === 'register') setActiveTab('register');
    else if (action === 'login') setActiveTab('login');
    if (email) {
      setRegisterForm((prev) => ({ ...prev, email }));
      setLoginForm((prev) => ({ ...prev, email }));
    }
  }, []);

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    setIsSendingReset(true);
    try {
      await authApi.requestPasswordReset(forgotEmail, 'client');
      setResetSent(true);
      toast.success('Lien de réinitialisation envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await authApi.login({
        email: loginForm.email,
        password: loginForm.password,
        role: 'client',
      });
      
      const userData = {
        id: response.data.user_id,
        role: response.data.role,
        name: response.data.name,
        company_id: response.data.company_id,
        account_status: response.data.account_status,
      };
      
      login(userData, response.data.token);
      
      // Check account status and show appropriate message
      if (response.data.account_status === 'pending') {
        toast.info('Votre compte est en attente de validation par l\'entreprise');
      } else {
        toast.success(`Bienvenue, ${response.data.name}!`);
      }
      
      navigate('/client');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Call register API
      const response = await fetch(`${API_BASE}/api/auth/register-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerForm.name,
          email: registerForm.email,
          phone: registerForm.phone,
          password: registerForm.password,
          company_id: companyData.id,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }
      
      setRegistrationSuccess(true);
      
      // Show appropriate message based on whether approval is required
      if (data.requires_approval) {
        toast.success('Compte créé ! En attente de validation par l\'entreprise.');
      } else {
        toast.success('Compte créé avec succès !');
      }
      
      // Auto-login after registration
      setTimeout(() => {
        setLoginForm({ email: registerForm.email, password: registerForm.password });
        setActiveTab('login');
        setRegistrationSuccess(false);
      }, 2000);
      
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'inscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Error state - company not found
  if (error || !companyData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">?</span>
            </div>
            <h2 className="text-xl font-heading font-semibold mb-2">Entreprise non trouvée</h2>
            <p className="text-muted-foreground mb-6">
              L'entreprise "{companySlug}" n'existe pas ou n'est plus active.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la page de connexion
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get company colors
  const primaryColor = companyData.primary_color || '#2F5233';
  const logoUrl = getAssetUrl(companyData.logo_url);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        {/* Company Logo & Name */}
        <div className="text-center mb-8 animate-fade-in">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={companyData.name} 
              className="h-20 mx-auto mb-4 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-20 h-20 rounded-2xl mx-auto mb-4 items-center justify-center text-white text-3xl font-bold ${logoUrl ? 'hidden' : 'flex'}`}
            style={{ backgroundColor: primaryColor }}
          >
            {companyData.name?.charAt(0)?.toUpperCase()}
          </div>
          <h1 className="text-2xl font-heading font-semibold">{companyData.name}</h1>
          <p className="text-muted-foreground mt-2">Espace Client</p>
        </div>

        <Card className="shadow-floating border-border/60 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="login" className="flex items-center gap-2">
                <LogIn className="w-4 h-4" />
                Connexion
              </TabsTrigger>
              <TabsTrigger value="register" className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Créer un compte
              </TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login">
              <CardHeader className="text-center pb-4">
                <CardDescription>
                  Connectez-vous à votre espace client
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      data-testid="client-login-email"
                      placeholder="votre@email.fr"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        data-testid="client-login-password"
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Forgot password link */}
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-primary hover:underline"
                      data-testid="forgot-password-link"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="client-login-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      'Se connecter'
                    )}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
            
            {/* Register Tab */}
            <TabsContent value="register">
              {registrationSuccess ? (
                <CardContent className="py-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Compte créé avec succès !</h3>
                    <p className="text-muted-foreground">
                      Vous allez être redirigé vers la page de connexion...
                    </p>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="text-center pb-4">
                    <CardDescription>
                      Créez votre compte client chez {companyData.name}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent>
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-name">Nom complet *</Label>
                        <Input
                          id="register-name"
                          type="text"
                          data-testid="client-register-name"
                          placeholder="Jean Dupont"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-email">Email *</Label>
                        <Input
                          id="register-email"
                          type="email"
                          data-testid="client-register-email"
                          placeholder="votre@email.fr"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-phone">Téléphone</Label>
                        <Input
                          id="register-phone"
                          type="tel"
                          data-testid="client-register-phone"
                          placeholder="06 12 34 56 78"
                          value={registerForm.phone}
                          onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-password">Mot de passe *</Label>
                        <div className="relative">
                          <Input
                            id="register-password"
                            type={showPassword ? 'text' : 'password'}
                            data-testid="client-register-password"
                            placeholder="••••••••"
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-confirm">Confirmer le mot de passe *</Label>
                        <Input
                          id="register-confirm"
                          type="password"
                          data-testid="client-register-confirm"
                          placeholder="••••••••"
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        />
                      </div>
                      
                      <Button
                        type="submit"
                        className="w-full rounded-full"
                        style={{ backgroundColor: primaryColor }}
                        data-testid="client-register-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Création...
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Créer mon compte
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="px-6 pb-4 pt-2 border-t text-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-3 h-3 inline mr-1" />
              Connexion standard
            </Link>
          </div>
        </Card>

        {/* Footer */}
        <div className="animate-fade-in mt-6" style={{ animationDelay: '200ms' }}>
          <FooterBranded />
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setForgotEmail('');
          setResetSent(false);
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>
              {resetSent 
                ? 'Un email de réinitialisation a été envoyé.'
                : 'Entrez votre email pour recevoir un lien de réinitialisation.'}
            </DialogDescription>
          </DialogHeader>
          
          {!resetSent ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  data-testid="forgot-email-input"
                  placeholder="votre@email.fr"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
              </div>
              <Button 
                className="w-full" 
                onClick={handleForgotPassword}
                disabled={isSendingReset}
              >
                {isSendingReset ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer le lien
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="py-4 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-muted-foreground">
                Vérifiez votre boîte de réception et suivez les instructions.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
